import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, fmtEur, fmtDate, fileUrl, uploadFile, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, Briefcase, Search, ShoppingCart, Paperclip, UserPlus, X, FileText, Zap, Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { CorsistaFields } from "../components/CorsistaFields";

const EMPTY_CORSISTA = { nome: "", cognome: "", codice_fiscale: "", email: "", telefono: "", cellulare: "", data_nascita: "", luogo_nascita: "", provincia_nascita: "", sesso: "", indirizzo: "", citta: "", cap: "", mansione: "", codice_ateco: "", tipo: "" };

const CATEGORIE = [
  { key: "aula", label: "Corsi Aula" },
  { key: "videoconferenza", label: "Corsi VideoConferenza" },
  { key: "elearning", label: "Corsi E-learning" },
  { key: "consulenza", label: "Consulenza Aziendale" },
];

export default function Catalog() {
  const { user } = useAuth();
  const isOwner = user.role !== "partner";
  const isRivenditore = user.role === "partner" && user.tipo_partner === "rivenditore";
  const rivPct = Number(user.rivendita_percent) || 50;
  const priceFor = (s) => {
    if (!isRivenditore) return s.prezzo_vendita;
    // Il rivenditore vede SOLO il prezzo a lui riservato: il campo dedicato se impostato,
    // altrimenti la % di rivendita calcolata sul prezzo di listino. Mai il prezzo di vendita standard.
    if (Number(s.prezzo_rivenditore) > 0) return s.prezzo_rivenditore;
    return Math.round((Number(s.prezzo_vendita) || 0) * rivPct) / 100;
  };
  const [services, setServices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");
  const [catTag, setCatTag] = useState("all");
  const [textTag, setTextTag] = useState("all");
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(null);
  const [editOrderId, setEditOrderId] = useState(null);
  const emptyQuickOrder = () => ({ corsista: { ...EMPTY_CORSISTA }, servizi: [], search: "" });
  const [quick, setQuick] = useState(emptyQuickOrder());
  const [ordersDocs, setOrdersDocs] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const o = location.state?.editOrder;
    if (o) {
      setEditOrderId(o.id);
      setForm({
        partner_id: o.partner_id || "",
        azienda_mode: o.azienda_id ? "existing" : "new",
        azienda_id: o.azienda_id || "", azienda_nome: o.azienda_id ? "" : (o.azienda_nome || ""),
        azienda_partita_iva: "", azienda_indirizzo: "", azienda_citta: "", azienda_cap: "",
        azienda_telefono: "", azienda_pec: "", azienda_codice_destinatario: "", azienda_codice_ateco: "",
        righe: (o.righe || []).map((r) => ({
          servizio_id: r.servizio_id, servizio_nome: r.servizio_nome, quantita: r.quantita || 1,
          corsisti: (r.corsisti || []).map((c) => ({ ...EMPTY_CORSISTA, ...c })),
        })),
        note: o.note || "", allegati: o.allegati || [],
      });
      setQuick(emptyQuickOrder());
      setOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/companies").then((r) => setCompanies(r.data));
    if (isOwner) api.get("/partners").then((r) => setPartners(r.data));
    api.get("/orders").then((r) => setOrdersDocs((r.data || []).filter((o) => (o.documenti_da_firmare || []).length))).catch(() => {});
  }, [isOwner]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return services.filter((s) => {
      const matchQ = !q || s.nome.toLowerCase().includes(q) || (s.tags || []).some((t) => t.toLowerCase().includes(q));
      const matchTag = textTag === "all" || (s.tags || []).some((t) => t === textTag);
      return matchQ && matchTag;
    });
  }, [services, search, textTag]);

  const allTags = useMemo(() => {
    const counts = {};
    services.forEach((s) => (s.tags || []).forEach((t) => { const k = (t || "").trim(); if (k) counts[k] = (counts[k] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [services]);

  const startOrder = (svc) => {
    setEditOrderId(null);
    setForm({
      partner_id: "",
      azienda_mode: companies.length ? "existing" : "new",
      azienda_id: "", azienda_nome: "", azienda_partita_iva: "", azienda_indirizzo: "",
      azienda_citta: "", azienda_cap: "", azienda_telefono: "", azienda_pec: "",
      azienda_codice_destinatario: "", azienda_codice_ateco: "",
      righe: [{ servizio_id: svc.id, servizio_nome: svc.nome, quantita: 1, corsisti: [{ ...EMPTY_CORSISTA }] }],
      note: "", allegati: [],
    });
    setQuick(emptyQuickOrder());
    setOpen(true);
  };

  const setRiga = (i, patch) => setForm((f) => ({ ...f, righe: f.righe.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const addRiga = () => setForm((f) => ({ ...f, righe: [...f.righe, { servizio_id: "", servizio_nome: "", quantita: 1, corsisti: [{ ...EMPTY_CORSISTA }] }] }));
  const removeRiga = (i) => setForm((f) => ({ ...f, righe: f.righe.filter((_, idx) => idx !== i) }));
  const onRigaService = (i, sid) => {
    const svc = services.find((s) => s.id === sid);
    setRiga(i, { servizio_id: sid, servizio_nome: svc ? svc.nome : "" });
  };

  const addCorsista = (ri) => setForm((f) => ({
    ...f, righe: f.righe.map((r, idx) => (idx === ri ? { ...r, corsisti: [...r.corsisti, { ...EMPTY_CORSISTA }] } : r)),
  }));
  const removeCorsista = (ri, ci) => setForm((f) => ({
    ...f, righe: f.righe.map((r, idx) => (idx === ri ? { ...r, corsisti: r.corsisti.filter((_, k) => k !== ci) } : r)),
  }));
  const setCorsista = (ri, ci, obj) => setForm((f) => ({
    ...f, righe: f.righe.map((r, idx) => (idx === ri
      ? { ...r, corsisti: r.corsisti.map((c, k) => (k === ci ? obj : c)) } : r)),
  }));

  const toggleQuickServizio = (sid) => setQuick((q) => ({
    ...q, servizi: q.servizi.includes(sid) ? q.servizi.filter((x) => x !== sid) : [...q.servizi, sid],
  }));
  const quickAddCorsista = () => {
    const c = quick.corsista;
    if (!c.nome && !c.cognome) return toast.error("Inserisci almeno nome e cognome del corsista");
    if (quick.servizi.length === 0) return toast.error("Seleziona almeno un corso");
    setForm((f) => {
      let righe = f.righe.filter((r) => r.servizio_id || (r.corsisti || []).some((x) => x.nome || x.cognome));
      quick.servizi.forEach((sid) => {
        const svc = services.find((s) => s.id === sid);
        const idx = righe.findIndex((r) => r.servizio_id === sid);
        if (idx === -1) righe = [...righe, { servizio_id: sid, servizio_nome: svc ? svc.nome : "", quantita: 1, corsisti: [{ ...c }] }];
        else righe = righe.map((r, k) => (k === idx ? { ...r, corsisti: [...(r.corsisti || []), { ...c }] } : r));
      });
      if (righe.length === 0) righe = [{ servizio_id: "", servizio_nome: "", quantita: 1, corsisti: [{ ...EMPTY_CORSISTA }] }];
      return { ...f, righe };
    });
    toast.success(`Corsista aggiunto a ${quick.servizi.length} cors${quick.servizi.length === 1 ? "o" : "i"}`);
    setQuick(emptyQuickOrder());
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: troppo grande (max 10 MB)`); continue; }
        const ref = await uploadFile(file);
        setForm((f) => ({ ...f, allegati: [...f.allegati, ref] }));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };
  const removeAllegato = (id) => setForm((f) => ({ ...f, allegati: f.allegati.filter((a) => a.id !== id) }));

  const submit = async () => {
    if (isOwner && !form.partner_id) return toast.error("Seleziona il partner");
    if (form.azienda_mode === "existing" && !form.azienda_id) return toast.error("Seleziona l'azienda cliente");
    if (form.azienda_mode === "new" && !form.azienda_nome.trim()) return toast.error("Inserisci il nome della nuova azienda");
    const righe = form.righe.filter((r) => r.servizio_id);
    if (righe.length === 0) return toast.error("Aggiungi almeno un corso");
    try {
      const az = form.azienda_mode === "existing"
        ? { azienda_id: form.azienda_id, azienda_nome: companies.find((c) => c.id === form.azienda_id)?.nome || "" }
        : { azienda_id: "", azienda_nome: form.azienda_nome, azienda_partita_iva: form.azienda_partita_iva, azienda_indirizzo: form.azienda_indirizzo, azienda_citta: form.azienda_citta, azienda_cap: form.azienda_cap, azienda_telefono: form.azienda_telefono, azienda_pec: form.azienda_pec, azienda_codice_destinatario: form.azienda_codice_destinatario, azienda_codice_ateco: form.azienda_codice_ateco };
      const payload = {
        ...(isOwner ? { partner_id: form.partner_id } : {}),
        ...az,
        note: form.note,
        righe: righe.map((r) => ({
          servizio_id: r.servizio_id, quantita: parseFloat(r.quantita) || 1,
          corsisti: r.corsisti.filter((c) => (c.nome || c.cognome || c.codice_fiscale)),
        })),
        allegati: form.allegati,
      };
      await api[editOrderId ? "put" : "post"](editOrderId ? `/orders/${editOrderId}` : "/orders", payload);
      toast.success(editOrderId ? "Ordine aggiornato con successo" : "Ordine inviato con successo");
      setOpen(false); setForm(null); setEditOrderId(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Catalogo Corsi e Servizi</h1>
          <p className="text-sm text-slate-500 mt-1">Sfoglia il catalogo e invia direttamente un ordine con i dati dei corsisti e dell'azienda.</p>
          {isRivenditore && (
            <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200" data-testid="catalog-rivenditore-badge">
              Catalogo Rivenditori · prezzi a te riservati
            </span>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="catalog-search"
            placeholder="Cerca corso o servizio…" className="pl-9 bg-white" />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="catalog-search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center" data-testid="catalog-tags">
          <span className="text-xs text-slate-400">Modalità:</span>
          {[{ key: "all", label: "Tutte" }, ...CATEGORIE].map(({ key, label }) => {
            const active = catTag === key;
            const count = key === "all" ? filtered.length : filtered.filter((s) => (s.categoria || "aula") === key).length;
            return (
              <button key={key} type="button" onClick={() => setCatTag(key)} data-testid={`catalog-tag-${key}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-dusty-600 text-white border-dusty-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {label} <span className="opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center" data-testid="catalog-text-tags">
            <span className="text-xs text-slate-400">Argomenti:</span>
            <button type="button" onClick={() => setTextTag("all")} data-testid="catalog-texttag-all"
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${textTag === "all" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              Tutti
            </button>
            {allTags.map(([tag, count]) => {
              const active = textTag === tag;
              return (
                <button key={tag} type="button" onClick={() => setTextTag(active ? "all" : tag)} data-testid={`catalog-texttag-${tag}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                  {tag} <span className="opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {ordersDocs.length > 0 && (
        <Card className="border-dusty-200 bg-dusty-50/40 p-5" data-testid="docs-da-firmare-section">
          <h2 className="text-lg font-display font-semibold text-slate-800 mb-1 flex items-center gap-2"><FileText className="h-5 w-5 text-dusty-600" /> Documenti da firmare ricevuti</h2>
          <p className="text-sm text-slate-500 mb-4">Scarica i documenti inviati dal titolare, falli firmare ai corsisti e ricaricali firmati in «I Miei Attestati».</p>
          <div className="space-y-3">
            {ordersDocs.map((o) => (
              <div key={o.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`docs-order-${o.id}`}>
                <div className="text-sm font-medium text-slate-800">{o.azienda_nome || "Ordine"} <span className="text-xs font-normal text-slate-400">· Ordine #{o.numero} · {fmtDate(o.created_at)}</span></div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {o.documenti_da_firmare.map((d) => (
                    <a key={d.id} href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-dusty-200 text-dusty-700 hover:bg-dusty-50" data-testid={`docs-file-${d.id}`}><FileText className="h-3.5 w-3.5" />{d.original_filename}</a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="space-y-8">
        {CATEGORIE.map(({ key, label }) => {
          if (catTag !== "all" && catTag !== key) return null;
          const list = filtered.filter((s) => (s.categoria || "aula") === key);
          if (list.length === 0) return null;
          return (
            <div key={key} data-testid={`catalog-group-${key}`}>
              <h2 className="text-lg font-display font-semibold text-slate-800 mb-3 flex items-center gap-2">
                {label}<span className="text-xs font-normal text-slate-400">({list.length})</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {list.map((s) => (
                  <Card key={s.id} className="border-slate-200 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-shadow" data-testid={`catalog-card-${s.id}`}>
                    <div className="relative aspect-video bg-dusty-50 overflow-hidden">
                      {s.image_path ? (
                        <img loading="lazy" decoding="async" src={fileUrl(s.image_thumb_path || s.image_path)} alt={s.nome} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-dusty-300"><Briefcase className="h-12 w-12" /></div>
                      )}
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="font-semibold text-slate-800 leading-snug">{s.nome}</div>
                      <div className="mt-1 text-sm text-dusty-700 font-semibold">
                        {fmtEur(priceFor(s))}
                        {isRivenditore && (
                          <span className="ml-1.5 text-[11px] font-medium text-violet-600 align-middle" data-testid={`catalog-price-riservato-${s.id}`}>prezzo riservato</span>
                        )}
                      </div>
                      {s.tags?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {s.tags.map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t}</span>)}
                        </div>
                      )}
                      <Button onClick={() => startOrder(s)} data-testid={`order-btn-${s.id}`}
                        className="mt-4 w-full bg-dusty-600 hover:bg-dusty-700 text-white">
                        <ShoppingCart className="h-4 w-4 mr-1.5" /> Ordina
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
        {(() => {
          const visibleCount = catTag === "all" ? filtered.length : filtered.filter((s) => (s.categoria || "aula") === catTag).length;
          return visibleCount === 0 && <div className="text-slate-400 py-12 text-center">Nessun corso trovato.</div>;
        })()}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editOrderId ? "Modifica Ordine" : "Nuovo Ordine"}</DialogTitle>
            <DialogDescription>Indica azienda, corsi e dati dei corsisti. Puoi allegare documenti (PDF, immagini, Word, Excel).</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4 py-1 max-h-[70vh] overflow-y-auto pr-1">
              {isOwner && (
                <div>
                  <Label>Partner</Label>
                  <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="order-partner"><SelectValue placeholder="Seleziona partner" /></SelectTrigger>
                    <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                <div className="flex gap-2">
                  <button type="button" onClick={() => setForm({ ...form, azienda_mode: "existing" })} data-testid="azienda-mode-existing"
                    className={`text-xs px-3 py-1.5 rounded-lg border ${form.azienda_mode === "existing" ? "bg-dusty-600 text-white border-dusty-600" : "bg-white text-slate-600 border-slate-200"}`}>Azienda esistente</button>
                  <button type="button" onClick={() => setForm({ ...form, azienda_mode: "new" })} data-testid="azienda-mode-new"
                    className={`text-xs px-3 py-1.5 rounded-lg border ${form.azienda_mode === "new" ? "bg-dusty-600 text-white border-dusty-600" : "bg-white text-slate-600 border-slate-200"}`}>Nuova azienda</button>
                </div>
                {form.azienda_mode === "existing" ? (
                  <Select value={form.azienda_id} onValueChange={(v) => setForm({ ...form, azienda_id: v })}>
                    <SelectTrigger data-testid="order-azienda"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    <Input value={form.azienda_nome} onChange={(e) => setForm({ ...form, azienda_nome: e.target.value })} placeholder="Ragione Sociale *" data-testid="order-azienda-nome" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={form.azienda_partita_iva} onChange={(e) => setForm({ ...form, azienda_partita_iva: e.target.value })} placeholder="Partita IVA" data-testid="order-azienda-piva" />
                      <Input value={form.azienda_indirizzo} onChange={(e) => setForm({ ...form, azienda_indirizzo: e.target.value })} placeholder="Via / n°" data-testid="order-azienda-indirizzo" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input value={form.azienda_citta} onChange={(e) => setForm({ ...form, azienda_citta: e.target.value })} placeholder="Città" data-testid="order-azienda-citta" />
                      <Input value={form.azienda_cap} onChange={(e) => setForm({ ...form, azienda_cap: e.target.value })} placeholder="CAP" data-testid="order-azienda-cap" />
                      <Input value={form.azienda_telefono} onChange={(e) => setForm({ ...form, azienda_telefono: e.target.value })} placeholder="Telefono" data-testid="order-azienda-tel" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={form.azienda_pec} onChange={(e) => setForm({ ...form, azienda_pec: e.target.value })} placeholder="PEC" data-testid="order-azienda-pec" />
                      <Input value={form.azienda_codice_destinatario} onChange={(e) => setForm({ ...form, azienda_codice_destinatario: e.target.value })} placeholder="Codice destinatario (SDI)" data-testid="order-azienda-sdi" />
                    </div>
                    <Input value={form.azienda_codice_ateco} onChange={(e) => setForm({ ...form, azienda_codice_ateco: e.target.value })} placeholder="Codice ATECO" data-testid="order-azienda-ateco" />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-700 font-semibold">Corsi / Servizi</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addRiga} data-testid="order-add-line"><Plus className="h-4 w-4 mr-1" /> Aggiungi corso</Button>
                </div>

                <div className="rounded-lg border border-dusty-200 bg-dusty-50/50 p-3" data-testid="order-quick-add-panel">
                  <div className="flex items-center gap-1.5 mb-2 text-dusty-800 font-semibold text-sm">
                    <Zap className="h-4 w-4" /> Aggiunta rapida: uno stesso corsista su più corsi
                  </div>
                  <p className="text-[11px] text-slate-500 mb-2">Inserisci il corsista una sola volta, spunta tutti i corsi che deve frequentare e aggiungili in un colpo solo.</p>
                  <div className="rounded-md border border-slate-200 bg-white p-2 mb-2">
                    <CorsistaFields value={quick.corsista} onChange={(next) => setQuick((q) => ({ ...q, corsista: next }))} showAteco showTipo idPrefix="order-quick-corsista" />
                  </div>
                  <div className="mb-2">
                    <Input value={quick.search} onChange={(e) => setQuick((q) => ({ ...q, search: e.target.value }))} placeholder="Cerca un corso da spuntare…" className="h-9 bg-white" data-testid="order-quick-search" />
                  </div>
                  <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100" data-testid="order-quick-course-list">
                    {services
                      .filter((s) => !quick.search || s.nome.toLowerCase().includes(quick.search.toLowerCase()))
                      .map((s) => (
                        <label key={s.id} className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-slate-50 text-sm" data-testid={`order-quick-course-${s.id}`}>
                          <Checkbox checked={quick.servizi.includes(s.id)} onCheckedChange={() => toggleQuickServizio(s.id)} data-testid={`order-quick-course-check-${s.id}`} />
                          <span className="flex-1 text-slate-700">{s.nome}</span>
                          <span className="text-xs text-slate-400">{fmtEur(priceFor(s))}</span>
                        </label>
                      ))}
                    {services.filter((s) => !quick.search || s.nome.toLowerCase().includes(quick.search.toLowerCase())).length === 0 && (
                      <div className="px-2.5 py-3 text-xs text-slate-400 text-center">Nessun corso trovato</div>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-500">{quick.servizi.length} cors{quick.servizi.length === 1 ? "o" : "i"} selezionat{quick.servizi.length === 1 ? "o" : "i"}</span>
                    <Button type="button" size="sm" onClick={quickAddCorsista} disabled={quick.servizi.length === 0} className="bg-dusty-600 hover:bg-dusty-700 text-white h-8" data-testid="order-quick-add-btn">
                      <Check className="h-4 w-4 mr-1" /> Aggiungi ai corsi selezionati
                    </Button>
                  </div>
                </div>

                {form.righe.map((r, ri) => (
                  <div key={ri} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 relative" data-testid={`order-line-${ri}`}>
                    {form.righe.length > 1 && (
                      <button type="button" onClick={() => removeRiga(ri)} className="absolute top-2 right-2 text-rose-500 hover:text-rose-700" data-testid={`order-remove-line-${ri}`}><Trash2 className="h-4 w-4" /></button>
                    )}
                    <div className="grid grid-cols-3 gap-2 pr-6">
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Corso/Servizio</Label>
                        <Select value={r.servizio_id} onValueChange={(v) => onRigaService(ri, v)}>
                          <SelectTrigger className="mt-1 h-9" data-testid={`order-line-servizio-${ri}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                          <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Quantità</Label>
                        <Input type="number" min="1" value={r.quantita} onChange={(e) => setRiga(ri, { quantita: e.target.value })} data-testid={`order-line-qta-${ri}`} className="mt-1 h-9" />
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-600">Corsisti</span>
                        <button type="button" onClick={() => addCorsista(ri)} className="text-xs text-dusty-700 hover:text-dusty-900 inline-flex items-center gap-1" data-testid={`order-add-corsista-${ri}`}><UserPlus className="h-3.5 w-3.5" /> Aggiungi</button>
                      </div>
                      <div className="space-y-2">
                        {r.corsisti.map((c, ci) => (
                          <div key={ci} className="rounded-md border border-slate-200 bg-white p-2 relative" data-testid={`order-corsista-${ri}-${ci}`}>
                            {r.corsisti.length > 1 && (
                              <button type="button" onClick={() => removeCorsista(ri, ci)} className="absolute top-1.5 right-1.5 text-slate-300 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                            )}
                            <CorsistaFields
                              value={c}
                              onChange={(next) => setCorsista(ri, ci, next)}
                              showAteco
                              showTipo
                              idPrefix={`corsista-${ri}-${ci}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <Label className="text-slate-700 font-semibold">Allegati</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer" data-testid="order-file-label">
                    <Paperclip className="h-4 w-4" /> {uploading ? "Carico…" : "Aggiungi file"}
                    <input type="file" multiple className="hidden" onChange={onFiles} data-testid="order-file-input"
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" />
                  </label>
                  <span className="text-xs text-slate-400">PDF, immagini, Word, Excel · max 10 MB</span>
                </div>
                {form.allegati.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {form.allegati.map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5" data-testid={`order-allegato-${a.id}`}>
                        <span className="inline-flex items-center gap-1.5 text-slate-600 truncate"><FileText className="h-3.5 w-3.5 shrink-0" />{a.original_filename}</span>
                        <button type="button" onClick={() => removeAllegato(a.id)} className="text-slate-400 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label>Note</Label>
                <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note per l'ordine…" className="mt-1.5" data-testid="order-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={uploading} data-testid="order-submit" className="bg-dusty-600 hover:bg-dusty-700 text-white">Invia ordine</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
